import SelectDropDown from "@CubeComponents/Mui_Form/SelectDropDown";
import { isEstimateRequiredPermission, itemListSources } from "@Actions/estimation";
import CreateIcon from "@Components/StickySidebarViewer/assets/CreateIcon";
import EditIcon from "@Components/StickySidebarViewer/assets/EditIcon";
import ButtonComponent from "@CubeComponents/form/ButtonComponent";
import { BUTTON_TYPES } from "@CubeComponents/constants/globalVariable";
import IconComponent from "@CubeComponents/icons";
import { MdOutlineWrapText } from "react-icons/md";
import { folderPermissions } from "@Helpers/constants";
import _ from "lodash";
import { t } from "@i18n";

const { RESOURCE_MODIFY, RESOURCE_DOWNLOAD } = folderPermissions();

import type { SelectEventArgs } from "@CubeComponents/Mui_Form/SelectDropDown";
import type { EstimateCurrency } from "@ServerTypes/estimates/estimates.schema";

type EstimatePermissions = {
  userRole?: number;
};

type GridToolsArgs = {
  canEdit?: boolean;
  estimateData: EstimatePermissions;
  source: (typeof itemListSources)[keyof typeof itemListSources];
  estimateVersionId?: string;
  costDatabaseVersionId?: string;
  handleAddTopSelectionHandler?: (value: SelectEventArgs) => void;
  handleAddChildSelectionHandler?: (value: SelectEventArgs) => void;
};

/**
 * SlickGrid1 toolbar item: a free-form definition object passed straight to the grid's
 * toolbar layout engine. The shape changes per-id (`add_top`, `expand_all`, ColumnChooser,
 * etc.), so we declare only the fields we set here.
 */
type ToolbarItem = {
  align?: "Left" | "Right";
  id?: string;
  template?: () => React.ReactNode;
  disabled?: boolean;
  text?: string;
};

export const gridTools = ({
  canEdit = true,
  estimateData,
  source,
  estimateVersionId,
  costDatabaseVersionId,
  handleAddTopSelectionHandler,
  handleAddChildSelectionHandler,
}: GridToolsArgs): ToolbarItem[] => {
  let disabled = false;
  if (costDatabaseVersionId || estimateVersionId) {
    disabled = true;
  }

  const dropDownButtonItems = [
    { text: t("Add New Item"), id: "estimateItem" },
    {
      text: t("Add Item from Cost Database"),
      id: "costDatabaseItem",
      disabled: !_.get(estimateData, "costDatabase.id"),
    },
  ];

  // Button Templates
  const addTopButtonTemplate = () =>
    source === itemListSources.ESTIMATE ? (
      <div className="ms-4 w-32">
        <SelectDropDown
          allowButtonPadding={false}
          buttonType="dropdownBackgroundButton"
          select={handleAddTopSelectionHandler}
          items={dropDownButtonItems}
        >
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="add_top" disabled={!canEdit}>
            <span className="inline-block">
              <div className="flex items-center gap-1" data-testid="AddTopButton">
                <CreateIcon size="15" className="me-1" />
                <span>{t("Add Top")}</span>
              </div>
            </span>
          </ButtonComponent>
        </SelectDropDown>
      </div>
    ) : (
      <div className="me-6">
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="add_top" disabled={!canEdit}>
          <span className="flex items-center gap-1" data-testid="AddTopButton">
            <CreateIcon size="15" className="me-1" />
            {t("Add Top")}
          </span>
        </ButtonComponent>
      </div>
    );

  const addChildButtonTemplate = () =>
    source === itemListSources.ESTIMATE ? (
      <div className="ms-4 w-36">
        <SelectDropDown
          buttonType="dropdownBackgroundButton"
          allowButtonPadding={false}
          select={handleAddChildSelectionHandler}
          items={dropDownButtonItems}
        >
          <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="add_child" disabled={!canEdit}>
            <span className="inline-block">
              <div className="flex items-center gap-1" data-testid="AddChildButton">
                <CreateIcon size="15" className="me-1" />
                <span>{t("Add Child")}</span>
              </div>
            </span>
          </ButtonComponent>
        </SelectDropDown>
      </div>
    ) : (
      <div className="">
        <ButtonComponent buttonType={BUTTON_TYPES.BACKGROUND_BUTTON} id="add_child" disabled={!canEdit}>
          <span className="flex items-center gap-1" data-testid="AddChildButton">
            <CreateIcon size="15" className="me-1" />
            {t("Add Child")}
          </span>
        </ButtonComponent>
      </div>
    );

  const editButtonTemplate = () => (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="edit_item" disabled={!canEdit}>
        <span className="flex items-center gap-1" data-testid="editButton">
          <EditIcon size="15" className="me-1" />
          {t("Edit")}
        </span>
      </ButtonComponent>
    </div>
  );

  const removeBtnTemplate = () => (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="delete" disabled={!canEdit}>
        <span className="flex items-center gap-1" data-testid="deleteButton">
          <IconComponent name="delete" width="1.25rem" height="1.25rem" />
          {t("Delete")}
        </span>
      </ButtonComponent>
    </div>
  );

  const updateButtonTemplate = () => (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="update" disabled={!canEdit}>
        <span className="flex items-center gap-1" data-testid="updateButton">
          <IconComponent name="Update" width="1rem" height="1rem" />
          {t("Update")}
        </span>
      </ButtonComponent>
    </div>
  );

  const cancelButtonTemplate = () => (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="cancel" disabled={!canEdit}>
        <span className="flex items-center gap-1" data-testid="cancelButton">
          <IconComponent name="Cancel" width="1rem" height="1rem" />
          {t("Cancel")}
        </span>
      </ButtonComponent>
    </div>
  );

  const changeTypeButtonTemplate = () => (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="changeType" disabled={!canEdit}>
        <span className="flex items-center gap-1" data-testid="changeTypeButton">
          <IconComponent name="changeType" width="1rem" height="1rem" />
          {t("Change Type")}
        </span>
      </ButtonComponent>
    </div>
  );

  const expandButtonTemplate = () => (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="expand_all">
        <span className="flex items-center gap-1" data-testid="expandAllButton">
          <IconComponent name="Expand" width="1rem" height="1rem" />
          {t("Expand All")}
        </span>
      </ButtonComponent>
    </div>
  );

  const collapseButtonTemplate = () => (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="collapse_all">
        <span className="flex items-center gap-1" data-testid="collapseAllButton">
          <IconComponent name="Collapse" width="1rem" height="1rem" />
          {t("Collapse All")}
        </span>
      </ButtonComponent>
    </div>
  );

  const exportExcelButtonTemplate = () => (
    <div>
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="export_excel">
        <span className="flex items-center gap-1" data-testid="exportExcelButton">
          <IconComponent name="ExcelExportIcon" width="1rem" height="1rem" />
          {t("Export Excel")}
        </span>
      </ButtonComponent>
    </div>
  );

  const exportPDFButtonTemplate = () => (
    <div>
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="export_pdf">
        <span className="flex items-center gap-1" data-testid="exportPDFButton">
          <IconComponent name="PdfExport" width="1rem" height="1rem" />
          {t("Export PDF")}
        </span>
      </ButtonComponent>
    </div>
  );

  const modifyTotalButtonTemplate = () => (
    <div className="" data-testid="modifyTotalButton">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="modify_total" disabled={!canEdit}>
        <span>{t("Modify Total")}</span>
      </ButtonComponent>
    </div>
  );

  const wrapTextButtonTemplate = () => (
    <div className="">
      <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="wrap_text">
        <span className="flex items-center gap-1" data-testid="wrapTextButton">
          <MdOutlineWrapText />
          {t("Wrap Text")}
        </span>
      </ButtonComponent>
    </div>
  );

  function ImportExcelButtonTemplate() {
    return (
      <div className="">
        <ButtonComponent buttonType={BUTTON_TYPES.TEXT_BUTTON} id="importExcel" disabled={!canEdit}>
          <span className="flex items-center gap-1" data-testid="importExcelButton">
            <IconComponent name="ImportExcel" width="1rem" height="1rem" />
            {t("Import Excel")}
          </span>
        </ButtonComponent>
      </div>
    );
  }

  if (source === itemListSources.ADD_ITEM_POPUP || source === itemListSources.ESTIMATE_ATTACHED_CD_PREVIEW) {
    return [
      {
        align: "Left" as const,
        id: "expand_all",
        template: expandButtonTemplate,
      },
      {
        align: "Left" as const,
        id: "collapse_all",
        template: collapseButtonTemplate,
      },
      {
        align: "Left" as const,
        id: "wrap_text",
        template: wrapTextButtonTemplate,
      },
    ];
  }

  let list: ToolbarItem[] = [];

  if (!disabled) {
    list = [
      {
        align: "Left" as const,
        template: addTopButtonTemplate,
        id: "add_top",
        disabled:
          !canEdit || source === itemListSources.ESTIMATE
            ? !isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id)
            : false,
      },
      {
        text: t("Add Child"),
        align: "Left" as const,
        template: addChildButtonTemplate,
        id: "add_child",
        disabled: true,
      },
      {
        align: "Left" as const,
        template: editButtonTemplate,
        id: "edit_item",
        disabled: true,
      },
      {
        align: "Left" as const,
        template: removeBtnTemplate,
        id: "delete",
        disabled: true,
      },
      {
        align: "Left" as const,
        template: updateButtonTemplate,
        id: "update",
        disabled: true,
      },
      {
        align: "Left" as const,
        template: cancelButtonTemplate,
        id: "cancel",
        disabled: true,
      },
    ];
  }

  if (source == itemListSources.ESTIMATE && !disabled) {
    list.push({
      align: "Left" as const,
      template: changeTypeButtonTemplate,
      id: "changeType",
      disabled: true,
    });
  }

  list = [
    ...list,
    {
      align: "Left" as const,
      id: "expand_all",
      template: expandButtonTemplate,
    },
    {
      align: "Left" as const,
      id: "collapse_all",
      template: collapseButtonTemplate,
    },
    {
      align: "Left" as const,
      id: "wrap_text",
      template: wrapTextButtonTemplate,
    },
  ];

  if ((source == itemListSources.ESTIMATE || source == itemListSources.COST_DATABASE) && !disabled) {
    list.push({
      align: "Left" as const,
      id: "importExcel",
      template: ImportExcelButtonTemplate,
    });
  }

  if (source === itemListSources.ESTIMATE && !disabled) {
    list.push({
      align: "Left" as const,
      template: modifyTotalButtonTemplate,
      id: "modify_total",
      disabled: !isEstimateRequiredPermission(estimateData, RESOURCE_MODIFY.id),
    });
  }

  if (source === itemListSources.ESTIMATE) {
    list = [
      ...list,
      {
        align: "Left" as const,
        id: "export_excel",
        template: exportExcelButtonTemplate,
        disabled: !isEstimateRequiredPermission(estimateData, RESOURCE_DOWNLOAD.id),
      },
      {
        align: "Left" as const,
        template: exportPDFButtonTemplate,
        id: "export_pdf",
        disabled: !isEstimateRequiredPermission(estimateData, RESOURCE_DOWNLOAD.id),
      },
    ];
  }

  list.push({
    text: "ColumnChooser",
  });

  return list;
};

export const getNoAccessPage = (module: string) => {
  return (
    <div className="w-full h-full flex gap-4 flex-col items-center justify-center overflow-auto">
      <IconComponent name="NoAccess" width="8rem" height="8rem" />
      <span className="text-xl font-semibold">{`You Don’t Have Access to This ${module}`}</span>
    </div>
  );
};

type CurrencyData = EstimateCurrency;

// SlickGrid Formatter exception (rule 5): SlickGrid column templates pre-date strict typing
// in this codebase and accept open-ended `Formatter` callbacks.
import type { Formatter } from "@slickgrid-universal/common";
type TemplateFn = Formatter;

type GridColumnsArgs = {
  allowCheckboxSelection?: boolean;
  source: string;
  currencyData: CurrencyData;
  costDatabaseCurrencySymbol?: string;
  serialTemplate?: TemplateFn;
  codeEditTemplate?: TemplateFn;
  HSN_SACEitTemplate?: TemplateFn;
  descriptionEditTemplate?: TemplateFn;
  quantityTemplate?: TemplateFn;
  quantityEditTemplate?: TemplateFn;
  unitEditTemplate?: TemplateFn;
  unitTemplate?: TemplateFn;
  rateEditTemplate?: TemplateFn;
  rateTemplate?: TemplateFn;
  amountTemplate?: TemplateFn;
  amountEditTemplate?: TemplateFn;
  costDatabaseRateTemplate?: TemplateFn;
  usedQuantityTemplate?: TemplateFn;
  remainingQuantityTemplate?: TemplateFn;
  totalQuantityTemplate?: TemplateFn;
  remainingAmountTemplate?: TemplateFn;
  showInfoColumn?: boolean;
  detailTemplate?: TemplateFn;
};

export const getGridColumns = ({
  allowCheckboxSelection,
  source,
  currencyData,
  costDatabaseCurrencySymbol,
  serialTemplate,
  codeEditTemplate,
  HSN_SACEitTemplate,
  descriptionEditTemplate,
  quantityTemplate,
  quantityEditTemplate,
  unitEditTemplate,
  unitTemplate,
  rateEditTemplate,
  rateTemplate,
  amountTemplate,
  amountEditTemplate,
  costDatabaseRateTemplate,
  usedQuantityTemplate,
  remainingQuantityTemplate,
  totalQuantityTemplate,
  remainingAmountTemplate,
  showInfoColumn = false,
  detailTemplate,
}: GridColumnsArgs) => {
  const columnsList =
    source === itemListSources.ESTIMATE ||
    source === itemListSources.BOQ_PRINT ||
    source === itemListSources.LINK_BOQ_ITEMS ||
    source === itemListSources.PREVIEW_BOQ_ITEMS
      ? [
          {
            field: "itemId",
            headerText: "",
            width: "0",
            visible: false,
            showInColumnChooser: false,
            isPrimaryKey: true,
            allowFiltering: false,
            allowEditing: false,
            allowSorting: false,
          },
          ...(allowCheckboxSelection
            ? [
                {
                  type: "checkbox",
                  headerTextAlign: "center",
                  width: "100",
                  showInColumnChooser: false,
                  allowFiltering: false,
                  allowSorting: false,
                  allowEditing: false,
                },
              ]
            : []),
          ...(showInfoColumn
            ? [
                {
                  field: "sourceFormNo",
                  headerText: t("Form No (info)"),
                  allowEditing: false,
                  allowSorting: false,
                  allowFiltering: false,
                  template: detailTemplate,
                  width: "120",
                },
              ]
            : []),
          {
            field: "serial",
            headerText: t("S. No."),
            isIdentity: true,
            template: serialTemplate,
            allowFiltering: true,
            allowEditing: false,
            allowSorting: true,
            width: source == itemListSources.BOQ_PRINT ? 80 : 100,
          },
          {
            field: "code",
            headerText: t("Code"),
            type: "string",
            editTemplate: codeEditTemplate,
            allowFiltering: true,
            allowEditing: true,
            allowSorting: true,
            width: source == itemListSources.BOQ_PRINT ? 80 : 100,
          },
          {
            field: "HSN_SAC",
            headerText: t("HSN/SAC"),
            editTemplate: HSN_SACEitTemplate,
            allowFiltering: true,
            allowEditing: true,
            allowSorting: true,
            visible: false,
          },
          {
            field: "description",
            headerText: t("Description"),
            editTemplate: descriptionEditTemplate,
            allowFiltering: true,
            allowEditing: false,
            allowSorting: true,
            width: source == itemListSources.BOQ_PRINT ? 350 : null,
          },
          ...(source === itemListSources.LINK_BOQ_ITEMS
            ? [
                {
                  field: "originalQuantity",
                  headerText: t("Total Quantity"),
                  allowFiltering: false,
                  allowEditing: false,
                  allowSorting: false,
                  template: totalQuantityTemplate,
                  width: 120,
                },
                {
                  field: "availableQuantity",
                  headerText: t("Available Quantity"),
                  allowFiltering: false,
                  allowEditing: false,
                  allowSorting: false,
                  template: remainingQuantityTemplate,
                  width: 120,
                },
              ]
            : []),
          ...(source === itemListSources.PREVIEW_BOQ_ITEMS
            ? [
                {
                  field: "totalQuantity",
                  headerText: t("Total Quantity"),
                  clipMode: "EllipsisWithTooltip",
                  allowFiltering: false,
                  allowEditing: false,
                  allowSorting: false,
                  template: totalQuantityTemplate,
                  width: 150,
                },
                {
                  field: "availableQuantity",
                  headerText: t("Available Quantity"),
                  clipMode: "EllipsisWithTooltip",
                  allowFiltering: false,
                  allowEditing: false,
                  allowSorting: false,
                  template: remainingQuantityTemplate,
                  width: 170,
                },
                {
                  field: "totalUsedQuantity",
                  headerText: t("Total Quantity Used"),
                  allowFiltering: false,
                  clipMode: "EllipsisWithTooltip",
                  allowEditing: false,
                  allowSorting: false,
                  template: usedQuantityTemplate,
                  width: 200,
                },
              ]
            : []),
          {
            field: "quantity",
            headerText: t("Quantity"),
            template: quantityTemplate,
            editTemplate: quantityEditTemplate,
            allowFiltering: false,
            allowEditing: true,
            allowSorting: false,
            width: source == itemListSources.BOQ_PRINT ? 150 : null,
          },
          {
            field: "purchaseUnit",
            headerText: t("Unit"),
            template: unitTemplate,
            editTemplate: unitEditTemplate,
            allowFiltering: false,
            allowEditing: true,
            allowSorting: true,
            width: source == itemListSources.BOQ_PRINT ? 150 : null,
          },
          {
            field: "rate",
            headerText: `${t("Rate")} (${currencyData.selectedSymbol.name})`,
            template: rateTemplate,
            editTemplate: rateEditTemplate,
            allowFiltering: false,
            allowEditing: true,
            allowSorting: false,
            width: 120,
          },
          ...(source === itemListSources.PREVIEW_BOQ_ITEMS
            ? [
                {
                  field: "linkedAmount",
                  headerText: `${t("Amount used in this form")} (${currencyData.selectedSymbol.name})`,
                  allowFiltering: false,
                  allowEditing: false,
                  allowSorting: false,
                  width: 150,
                },
              ]
            : []),
          ...(source === itemListSources.PREVIEW_BOQ_ITEMS || source === itemListSources.LINK_BOQ_ITEMS
            ? [
                {
                  field: "originalAmount",
                  headerText: `${t("Total Amount")} (${currencyData.selectedSymbol.name})`,
                  allowFiltering: false,
                  allowEditing: false,
                  allowSorting: false,
                  template: amountTemplate,
                  width: 150,
                },
                {
                  field: "remainingAmount",
                  headerText: `${t("Remaining Amount")} (${currencyData.selectedSymbol.name})`,
                  allowFiltering: false,
                  allowEditing: false,
                  allowSorting: false,
                  width: 150,
                  template: remainingAmountTemplate,
                },
              ]
            : []),
          ...(source !== itemListSources.PREVIEW_BOQ_ITEMS && source !== itemListSources.LINK_BOQ_ITEMS
            ? [
                {
                  field: "amount",
                  headerText: `${t("Amount")} (${currencyData.selectedSymbol.name})`,
                  isIdentity: true,
                  allowEditing: false,
                  allowSorting: false,
                  allowFiltering: false,
                  template: amountTemplate,
                  editTemplate: amountEditTemplate,
                  width: source == itemListSources.BOQ_PRINT ? 150 : null,
                },
              ]
            : []),
        ]
      : [
          {
            field: "itemId",
            headerText: "",
            width: "0",
            visible: false,
            showInColumnChooser: false,
            isPrimaryKey: true,
            allowFiltering: false,
            allowEditing: false,
            allowSorting: false,
          },
          ...(allowCheckboxSelection
            ? [
                {
                  type: "checkbox",
                  headerTextAlign: "center",
                  width: "100",
                  showInColumnChooser: false,
                  allowFiltering: false,
                  allowSorting: false,
                  allowEditing: false,
                },
              ]
            : []),
          // {
          //   type: 'checkbox',
          //   headerTextAlign: 'center',
          //   textAlign: 'center',
          //   width: '100',
          //   showInColumnChooser: false,
          //   allowFiltering: false,
          //   allowSorting: false,
          //   allowEditing: false,
          // },
          {
            field: "code",
            headerText: t("Code"),
            editTemplate: codeEditTemplate,
            allowFiltering: true,
            allowEditing: true,
            allowSorting: true,
          },
          {
            field: "HSN_SAC",
            headerText: t("HSN/SAC"),
            editTemplate: HSN_SACEitTemplate,
            allowFiltering: true,
            allowEditing: true,
            allowSorting: true,
            visible: false,
          },
          {
            field: "description",
            headerText: t("Description"),
            editTemplate: descriptionEditTemplate,
            allowFiltering: true,
            allowEditing: true,
            allowSorting: true,
          },
          {
            field: "purchaseUnit",
            headerText: t("Unit"),
            template: unitTemplate,
            editTemplate: unitEditTemplate,
            allowFiltering: true,
            allowEditing: true,
            allowSorting: true,
          },
          {
            field: "rate",
            headerText:
              source === itemListSources.COST_DATABASE
                ? `Rate (${costDatabaseCurrencySymbol})`
                : `Rate (${currencyData.selectedSymbol.name})`,
            editTemplate: rateEditTemplate,
            template: costDatabaseRateTemplate,
            allowFiltering: false,
            allowEditing: true,
            allowSorting: true,
          },
        ];

  return columnsList;
};
